import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RefreshCw, Lock, Compass, LayoutGrid, Map as MapIcon, Clock, Moon, DoorOpen, WifiOff, ArrowUp } from "lucide-react";
import { openStatus, hasShowTonight } from "@/lib/hours";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CitySearch } from "@/components/CitySearch";
import { ListingCard } from "@/components/ListingCard";
import { ListingDetail } from "@/components/ListingDetail";
import { MapView } from "@/components/MapView";
import { TopRated } from "@/components/TopRated";
import { TopPhotos } from "@/components/TopPhotos";
import { LastNightPhotos } from "@/components/LastNightPhotos";
import { FeaturedStrip } from "@/components/FeaturedStrip";
import { CountdownBanner } from "@/components/CountdownBanner";
import { ListingSearch } from "@/components/ListingSearch";
import { SavedSearches } from "@/components/SavedSearches";
import { QueryResults } from "@/components/QueryResults";
import { PromoRedeem } from "@/components/PromoRedeem";
import { CATEGORIES, matchesQuery } from "@/lib/categories";
import { api, errorMessage, startCheckout, openBillingPortal } from "@/lib/api";
import { TrialCountdown } from "@/components/TrialCountdown";
import { useAuth } from "@/context/AuthContext";
import { milesBetween } from "@/lib/time";
import { LAST_CITY_KEY } from "@/components/BottomTabs";
import { pollWhileBuilding, isAppendOnly, SEARCH_TIMEOUT } from "@/lib/poll";

const SKELETON_IDS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"];
const LOADING_LINES = ["Asking the locals for you…", "Checking show times & hours…", "Finding hidden gems…", "Lining up directions…"];

function LoadingState() {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % LOADING_LINES.length), 2500); return () => clearInterval(t); }, []);
  return (
    <div data-testid="explore-loading">
      <p className="text-[#00E5A3] mb-6 flex items-center gap-2"><Compass className="h-4 w-4 animate-spin" /> {LOADING_LINES[i]} <span className="text-slate-500 text-sm">(first search of the day can take up to a minute)</span></p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {SKELETON_IDS.map((id) => <Skeleton key={id} className="h-80 rounded-2xl bg-[#151C2C]" />)}
      </div>
    </div>
  );
}

function Paywall({ message }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const addCard = async () => { setBusy(true); try { await (user.payment_locked ? openBillingPortal() : startCheckout(user.checkout_plan)); } catch (e) { toast.error(errorMessage(e)); setBusy(false); } };
  const title = user?.payment_locked ? "Your payment didn't go through" : user?.needs_card ? "Start your free trial" : "Your free trial is over";
  return (
    <div data-testid="paywall" className="rounded-3xl border border-[#FF3B6B]/40 bg-[#151C2C] p-10 text-center max-w-xl mx-auto">
      <Lock className="h-8 w-8 text-[#FF3B6B] mx-auto" />
      <h2 className="text-2xl font-bold mt-4">{title}</h2>
      <p className="text-slate-400 mt-2">{message}</p>
      {!user?.payment_locked && <div className="mt-6 text-left"><PromoRedeem onDone={() => window.location.reload()} /></div>}
      {user?.needs_card || user?.payment_locked ? (
        <Button data-testid="paywall-add-card" disabled={busy} onClick={addCard} className="mt-6 rounded-full h-12 px-8 bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold">
          {busy ? "Opening secure page…" : user.payment_locked ? "Update my card" : "Add card & start free trial"}
        </Button>
      ) : (
        <Button data-testid="paywall-subscribe" onClick={() => navigate("/pricing")} className="mt-6 rounded-full h-12 px-8 bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold">See plans</Button>
      )}
    </div>
  );
}

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const city = params.get("city") || "";
  const nearParam = params.get("near");
  const near = useMemo(() => {
    const [lat, lng] = (nearParam || "").split(",").map(Number);
    return Number.isFinite(lat) && Number.isFinite(lng) && nearParam ? { lat, lng } : null;
  }, [nearParam]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(null);
  const [pollKey, setPollKey] = useState(0);
  const [view, setView] = useState("grid");
  const [live, setLive] = useState({ openNow: false, tonight: false, walkIn: false });
  const [textQ, setTextQ] = useState("");
  const resultsRef = useRef(null);
  const [deepQ, setDeepQ] = useState("");
  const [pending, setPending] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const currentRef = useRef(null);
  const autoApply = useRef(false);
  const loadedCity = useRef("");

  useEffect(() => {
    if (!city) return;
    if (loadedCity.current !== city) {
      loadedCity.current = city; currentRef.current = null;
      setLoading(true); setData(null); setPending(null); setPaywall(null); setCat("all"); setTimedOut(false);
    }
    const show = (d) => { currentRef.current = d; setData(d); setPending(null); };
    return pollWhileBuilding(() => api.get("/search", { params: { city }, timeout: SEARCH_TIMEOUT }).then((r) => r.data), {
      onData: (d) => {
        setLoading(false); setReconnecting(false);
        const cur = currentRef.current;
        if (!cur || autoApply.current || isAppendOnly(cur.listings, d.listings)) show(d);
        else { setData({ ...cur, status: d.status }); currentRef.current = { ...cur, status: d.status }; setPending(d); }
        if (d.status !== "building") autoApply.current = false;
      },
      onRetry: () => setReconnecting(true),
      onTimeout: () => setTimedOut(true),
      onError: (e) => {
        setLoading(false); setReconnecting(false);
        if (e.response?.status === 402) setPaywall(errorMessage(e)); else toast.error(errorMessage(e));
      },
    });
  }, [city, pollKey]);

  const retryPoll = () => { setTimedOut(false); setPollKey((k) => k + 1); };

  const applyPending = () => {
    currentRef.current = pending; setData(pending); setPending(null);
    const top = resultsRef.current ? resultsRef.current.getBoundingClientRect().top + window.scrollY - 190 : 0;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  };

  useEffect(() => { setTextQ(""); setDeepQ(""); }, [city]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/search/refresh", null, { params: { city }, timeout: SEARCH_TIMEOUT });
      toast.success("Fetching the latest times — new results will appear shortly");
      autoApply.current = true;
      setPollKey((k) => k + 1);
    }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { if (data?.city) localStorage.setItem(LAST_CITY_KEY, data.city); }, [data?.city]);

  const pickCat = (id) => {
    setCat(id);
    const top = resultsRef.current ? resultsRef.current.getBoundingClientRect().top + window.scrollY - 190 : 0;
    if (window.scrollY > top) window.scrollTo({ top, behavior: "smooth" });
  };

  const cityName = data?.city || city;
  const lastLogged = useRef("");
  const logSearch = useCallback((raw) => {
    const q = raw.trim(), k = `${cityName}|${q.toLowerCase()}`;
    if (q.length < 3 || !cityName || lastLogged.current === k) return;
    lastLogged.current = k;
    api.post("/search/log", { city: cityName, q }).catch(() => {});
  }, [cityName]);
  const runDeep = (q) => { setDeepQ(q); logSearch(q); };
  const pickIdea = (q) => {
    setTextQ(q); setCat("all"); logSearch(q);
    setDeepQ((data?.listings || []).some((l) => matchesQuery(l, q)) ? "" : q);
  };

  useEffect(() => {
    const t = setTimeout(() => logSearch(textQ), 2000);
    return () => clearTimeout(t);
  }, [textQ, logSearch]);

  const counts = useMemo(() => {
    const c = { all: data?.listings.length || 0 };
    (data?.listings || []).forEach((l) => { c[l.category] = (c[l.category] || 0) + 1; });
    return c;
  }, [data]);
  const chips = useMemo(() => CATEGORIES.filter((c) => c.id === "all" || counts[c.id]), [counts]);
  const shown = useMemo(() => {
    const list = (data?.listings || []).filter((l) => cat === "all" || l.category === cat)
      .filter((l) => (!live.openNow || openStatus(l.hours) === true) && (!live.tonight || hasShowTonight(l)) && (!live.walkIn || !l.reservation_required))
      .filter((l) => !textQ.trim() || matchesQuery(l, textQ))
      .map((l) => (near && typeof l.lat === "number" && typeof l.lng === "number" ? { ...l, distance: milesBetween(near, l) } : l));
    return near ? [...list].sort((a, b) => (a.distance ?? 1e9) - (b.distance ?? 1e9)) : list;
  }, [data, cat, near, live, textQ]);
  const updated = data ? new Date(data.updated_at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" }) : "";

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <TrialCountdown />
      <div className="max-md:sticky max-md:top-16 z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 max-md:py-2 max-md:bg-[#0B0F19]/95 max-md:backdrop-blur-xl md:mx-0 md:px-0" data-testid="explore-sticky-search"><div className="max-w-2xl"><CitySearch key={city} initial={city} size="sm" loading={loading} onSearch={(c, pos) => setParams(pos ? { city: c, near: `${pos.lat},${pos.lng}` } : { city: c })} /></div></div>

      {!city && <p data-testid="explore-empty" className="mt-16 text-center text-slate-400">Type a city above to see everything happening there.</p>}

      {city && (
        <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{near ? <span data-testid="near-me-label" className="text-[#00E5A3]">Closest to you first · </span> : null}Things to do in</p>
            <h1 data-testid="explore-city-title" className="text-3xl sm:text-4xl font-extrabold tracking-tight">{data?.city || city}</h1>
          </div>
          {data && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex p-1 rounded-full bg-[#151C2C] border border-[#26334D]">
                {[["grid", LayoutGrid, "List"], ["map", MapIcon, "Map"]].map(([v, Icon, label]) => (
                  <button key={v} data-testid={`view-toggle-${v}`} onClick={() => setView(v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 max-md:py-2 rounded-full text-sm transition-colors ${view === v ? "bg-white text-[#0B0F19] font-semibold" : "text-slate-300 hover:text-white"}`}>
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
              <span data-testid="freshness-indicator" className="flex items-center gap-2 text-sm text-[#00E5A3] bg-[#00E5A3]/10 px-3 py-1.5 rounded-full">
                <span className="h-2 w-2 rounded-full bg-[#00E5A3] live-dot" /> Updated {updated}
              </span>
              {reconnecting && <span data-testid="search-reconnecting" className="text-xs text-[#FFB800] flex items-center gap-1.5"><WifiOff className="h-3.5 w-3.5" /> Weak signal — retrying…</span>}
              <Button data-testid="refresh-data-button" variant="outline" disabled={refreshing} onClick={refresh}
                className="rounded-full border-[#26334D] bg-transparent hover:bg-[#1E293B]">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Refreshing…" : "Refresh"}
              </Button>
            </div>
          )}
        </div>
      )}

      <CountdownBanner listings={data?.listings} onOpen={setOpen} />
      {data && <ListingSearch value={textQ} onChange={setTextQ} onDeepSearch={runDeep} onPick={pickIdea} listings={data.listings} city={cityName} />}
      {data && <SavedSearches city={cityName} listings={data.listings} textQ={textQ} onRun={runDeep} />}
      {data && (
        <div data-testid="category-chips" className="mt-4 md:mt-6 -mx-4 px-4 flex gap-2 overflow-x-auto no-scrollbar py-2 md:pt-0 lg:flex-wrap lg:overflow-visible max-md:sticky max-md:top-[130px] z-20 max-md:bg-[#0B0F19]/95 max-md:backdrop-blur-xl max-md:border-b max-md:border-[#26334D]/60">
          {chips.map((c) => (
            <button key={c.id} data-testid={`category-chip-${c.id}`} onClick={() => pickCat(c.id)}
              className={`shrink-0 flex items-center gap-1.5 text-sm px-4 py-2 max-md:py-2.5 rounded-full border transition-colors active:scale-95 ${cat === c.id ? "bg-[#FF3B6B] border-[#FF3B6B] text-white" : "bg-[#151C2C] border-[#26334D] text-slate-300 hover:border-slate-500"}`}>
              <c.icon className="h-4 w-4" /> {c.label} <span className="opacity-60 font-mono-vv text-xs">{counts[c.id]}</span>
            </button>
          ))}
        </div>
      )}
      {data && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {[["openNow", "filter-open-now", Clock, "Open now"], ["tonight", "filter-tonight", Moon, "Tonight"], ["walkIn", "filter-no-reservation", DoorOpen, "No reservation needed"]].map(([k, tid, Icon, label]) => (
            <button key={k} data-testid={tid} onClick={() => setLive((f) => ({ ...f, [k]: !f[k] }))}
              className={`flex items-center gap-1.5 text-sm px-4 py-1.5 max-md:py-2 rounded-full border transition-colors active:scale-95 ${live[k] ? "bg-[#00E5A3] border-[#00E5A3] text-[#0B0F19] font-semibold" : "border-dashed border-[#26334D] text-slate-300 hover:border-[#00E5A3]"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      )}

      <div className="mt-6" ref={resultsRef}>
        {timedOut && (
          <div data-testid="search-timeout" className="mb-5 rounded-2xl border border-[#FFB800]/40 bg-[#FFB800]/10 p-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[#FFB800]">This is taking longer than usual. {data?.listings?.length ? "Here's what we have so far." : "We're still working on it in the background."}</p>
            <Button data-testid="search-timeout-retry" size="sm" onClick={retryPoll} className="rounded-full bg-[#FFB800] text-[#0B0F19] hover:bg-[#FFB800]/90">Try again</Button>
          </div>
        )}
        {!timedOut && (loading || (data?.status === "building" && data.listings.length === 0)) && <LoadingState />}
        {!timedOut && data?.status === "building" && data.listings.length > 0 && (
          <p data-testid="building-indicator" className="mb-5 text-sm text-[#00E5A3] flex items-center gap-2">
            <Compass className="h-4 w-4 animate-spin" /> Finding more things to do — new spots will keep appearing…
          </p>
        )}
        {paywall && <Paywall message={paywall} />}
        {data && data.status !== "building" && shown.length === 0 && <p data-testid="no-results" className="text-slate-400 py-16 text-center">{textQ.trim() ? `No saved listings match “${textQ.trim()}”.` : live.openNow || live.tonight || live.walkIn ? "Nothing matches right now — try turning off some filters." : "Nothing found here yet. Try a nearby bigger city."}</p>}
        {data && deepQ && <QueryResults city={data.city || city} q={deepQ} onOpen={setOpen} onClose={() => setDeepQ("")} />}
        {data && textQ.trim() && !deepQ && (
          <p data-testid="deep-search-hint" className="mb-5 text-sm text-slate-400">
            {shown.length} {shown.length === 1 ? "match" : "matches"} below. Not what you need?{" "}
            <button data-testid="deep-search-button" onClick={() => runDeep(textQ.trim())} className="text-[#00E5A3] font-semibold hover:underline">Search all of {data.city || city} for “{textQ.trim()}”</button>
          </p>
        )}
        {data && !textQ.trim() && cat === "all" && <FeaturedStrip items={data.listings} city={cityName} onOpen={setOpen} />}
        {data && !textQ.trim() && cat === "all" && <LastNightPhotos city={cityName} />}
        {data && !textQ.trim() && cat === "all" && <TopPhotos city={cityName} />}
        {data && shown.length > 0 && <TopRated items={shown} onOpen={setOpen} />}
        {data && shown.length > 0 && view === "map" && <MapView items={shown} onOpen={setOpen} />}
        {data && shown.length > 0 && view === "grid" && (
          <div data-testid="listings-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {shown.map((item, i) => <ListingCard key={item.id} item={item} index={i} onOpen={setOpen} />)}
          </div>
        )}
      </div>
      <ListingDetail item={open} city={data?.city || city} onClose={() => setOpen(null)} />
      {pending && (
        <button data-testid="new-listings-pill" onClick={applyPending}
          className="fixed z-[60] left-1/2 -translate-x-1/2 top-[140px] md:top-20 rise flex items-center gap-2 rounded-full bg-[#00E5A3] text-[#0B0F19] font-semibold text-sm px-5 py-2.5 shadow-2xl active:scale-95 transition-transform">
          <ArrowUp className="h-4 w-4" /> New listings ready — tap to update
        </button>
      )}
    </main>
  );
}
